// i18next bootstrap for the sitewide UI-chrome i18n infrastructure (dashboard sidebars/header,
// navbar, Settings).
//
// Scope (deliberately not Next.js's built-in locale-routing `i18n` config in next.config.js,
// which would change the URL structure for every route sitewide -- too broad a blast radius):
// this wires a real, working i18next + react-i18next instance with 3 languages
// (English/Spanish/French) covering dashboard/navbar chrome + Settings UI strings
// (public/locales/<lng>/common.json), a persisted language choice (localStorage
// "smarttech-lang", mirroring context/Context.js's "histudy-theme" pattern), and a
// LanguageSwitcher component (my_components/Common/LanguageSwitcher.js, plus the compact
// navbar one at my_components/Header/Offcanvas/LanguageMenu.js).
import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import en from "../public/locales/en/common.json";
import es from "../public/locales/es/common.json";
import fr from "../public/locales/fr/common.json";
import de from "../public/locales/de/common.json";
import nl from "../public/locales/nl/common.json";
import pt from "../public/locales/pt/common.json";
import ru from "../public/locales/ru/common.json";
import zh from "../public/locales/zh/common.json";
import hi from "../public/locales/hi/common.json";
import esAR from "../public/locales/es-AR/common.json";
import ptBR from "../public/locales/pt-BR/common.json";
import ar from "../public/locales/ar/common.json";
import yo from "../public/locales/yo/common.json";
import ig from "../public/locales/ig/common.json";
import ha from "../public/locales/ha/common.json";

// Additional namespaces beyond "common" (the default "translation" namespace above) -- one per
// content domain from the i18n Pass 2 rollout (homepage, FAQ, blog, events, dashboard/auth
// extras, legal pages). Each is its own JSON file per language rather than folded into
// common.json, so multiple translation passes can work on different domains concurrently
// without every one of them racing to edit the same 13+ files. Not every domain has every
// language yet (blog and legal in particular are still catching up) -- only import what
// actually exists on disk; a language/domain combination that's missing here simply falls back
// to that namespace's English fallback text (the 2nd argument every t() call already provides),
// same as any other missing-key case, until its translation lands and gets added here.
import enHome from "../public/locales/en/home.json";
import esHome from "../public/locales/es/home.json";
import esARHome from "../public/locales/es-AR/home.json";
import frHome from "../public/locales/fr/home.json";
import deHome from "../public/locales/de/home.json";
import nlHome from "../public/locales/nl/home.json";
import ptHome from "../public/locales/pt/home.json";
import ruHome from "../public/locales/ru/home.json";
import zhHome from "../public/locales/zh/home.json";
import hiHome from "../public/locales/hi/home.json";
import yoHome from "../public/locales/yo/home.json";
import igHome from "../public/locales/ig/home.json";
import haHome from "../public/locales/ha/home.json";
import arHome from "../public/locales/ar/home.json";
import ptBRHome from "../public/locales/pt-BR/home.json";

import enFaq from "../public/locales/en/faq.json";
import esFaq from "../public/locales/es/faq.json";
import esARFaq from "../public/locales/es-AR/faq.json";
import frFaq from "../public/locales/fr/faq.json";
import deFaq from "../public/locales/de/faq.json";
import nlFaq from "../public/locales/nl/faq.json";
import ptFaq from "../public/locales/pt/faq.json";
import ruFaq from "../public/locales/ru/faq.json";
import zhFaq from "../public/locales/zh/faq.json";
import hiFaq from "../public/locales/hi/faq.json";
import yoFaq from "../public/locales/yo/faq.json";
import igFaq from "../public/locales/ig/faq.json";
import haFaq from "../public/locales/ha/faq.json";
import arFaq from "../public/locales/ar/faq.json";
import ptBRFaq from "../public/locales/pt-BR/faq.json";

import enBlog from "../public/locales/en/blog.json";
import esBlog from "../public/locales/es/blog.json";
import esARBlog from "../public/locales/es-AR/blog.json";
import frBlog from "../public/locales/fr/blog.json";
import deBlog from "../public/locales/de/blog.json";
import nlBlog from "../public/locales/nl/blog.json";
import ptBlog from "../public/locales/pt/blog.json";
import ruBlog from "../public/locales/ru/blog.json";
import zhBlog from "../public/locales/zh/blog.json";
import hiBlog from "../public/locales/hi/blog.json";

import enEvents from "../public/locales/en/events.json";
import esEvents from "../public/locales/es/events.json";
import esAREvents from "../public/locales/es-AR/events.json";
import frEvents from "../public/locales/fr/events.json";
import deEvents from "../public/locales/de/events.json";
import nlEvents from "../public/locales/nl/events.json";
import ptEvents from "../public/locales/pt/events.json";
import ruEvents from "../public/locales/ru/events.json";
import zhEvents from "../public/locales/zh/events.json";
import hiEvents from "../public/locales/hi/events.json";
import yoEvents from "../public/locales/yo/events.json";
import igEvents from "../public/locales/ig/events.json";
import haEvents from "../public/locales/ha/events.json";

import enDashboard from "../public/locales/en/dashboard.json";
import esDashboard from "../public/locales/es/dashboard.json";
import esARDashboard from "../public/locales/es-AR/dashboard.json";
import frDashboard from "../public/locales/fr/dashboard.json";
import deDashboard from "../public/locales/de/dashboard.json";
import nlDashboard from "../public/locales/nl/dashboard.json";
import ptDashboard from "../public/locales/pt/dashboard.json";
import ruDashboard from "../public/locales/ru/dashboard.json";
import zhDashboard from "../public/locales/zh/dashboard.json";
import hiDashboard from "../public/locales/hi/dashboard.json";
import yoDashboard from "../public/locales/yo/dashboard.json";
import igDashboard from "../public/locales/ig/dashboard.json";
import haDashboard from "../public/locales/ha/dashboard.json";

import enLegal from "../public/locales/en/legal.json";
import esLegal from "../public/locales/es/legal.json";
import esARLegal from "../public/locales/es-AR/legal.json";
import frLegal from "../public/locales/fr/legal.json";
import deLegal from "../public/locales/de/legal.json";
import nlLegal from "../public/locales/nl/legal.json";
import ptLegal from "../public/locales/pt/legal.json";
import ruLegal from "../public/locales/ru/legal.json";
import zhLegal from "../public/locales/zh/legal.json";
import hiLegal from "../public/locales/hi/legal.json";
import yoLegal from "../public/locales/yo/legal.json";
import igLegal from "../public/locales/ig/legal.json";
import haLegal from "../public/locales/ha/legal.json";

import yoBlog from "../public/locales/yo/blog.json";
import igBlog from "../public/locales/ig/blog.json";
import haBlog from "../public/locales/ha/blog.json";
import arBlog from "../public/locales/ar/blog.json";
import ptBRBlog from "../public/locales/pt-BR/blog.json";

import arEvents from "../public/locales/ar/events.json";
import ptBREvents from "../public/locales/pt-BR/events.json";

import arDashboard from "../public/locales/ar/dashboard.json";
import ptBRDashboard from "../public/locales/pt-BR/dashboard.json";

import arLegal from "../public/locales/ar/legal.json";
import ptBRLegal from "../public/locales/pt-BR/legal.json";

import enCertificate from "../public/locales/en/certificate.json";
import esCertificate from "../public/locales/es/certificate.json";
import esARCertificate from "../public/locales/es-AR/certificate.json";
import ptBRCertificate from "../public/locales/pt-BR/certificate.json";
import arCertificate from "../public/locales/ar/certificate.json";
import frCertificate from "../public/locales/fr/certificate.json";
import deCertificate from "../public/locales/de/certificate.json";
import nlCertificate from "../public/locales/nl/certificate.json";
import ptCertificate from "../public/locales/pt/certificate.json";
import ruCertificate from "../public/locales/ru/certificate.json";
import zhCertificate from "../public/locales/zh/certificate.json";
import hiCertificate from "../public/locales/hi/certificate.json";
import yoCertificate from "../public/locales/yo/certificate.json";
import igCertificate from "../public/locales/ig/certificate.json";
import haCertificate from "../public/locales/ha/certificate.json";

// Labels are each language's own name for itself (in its own script), not English glosses --
// that's what lets a reader who can't read English still find their language in the list.
//
// `dir` (defaults to "ltr" when omitted) matters for exactly one language here: Arabic. Setting
// it correctly gets the *text* flowing right-to-left properly (browsers handle Arabic script
// reading order automatically once the ancestor's `dir` attribute says so -- see
// applyDocumentDirection() below) -- it does NOT mirror this app's actual page layout (nav
// order, icon positions, sidebar side, etc.), which would need every component's CSS audited
// for direction-aware spacing/positioning, a much larger follow-up piece of work. Read: Arabic
// content is genuinely readable and correctly-directioned, but the surrounding chrome still
// visually reads left-to-right rather than being a fully mirrored RTL layout.
export const SUPPORTED_LANGUAGES = [
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "es-AR", label: "Español (Argentina)" },
  { code: "pt-BR", label: "Português (Brasil)" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
  { code: "nl", label: "Nederlands" },
  { code: "pt", label: "Português" },
  { code: "ru", label: "Русский" },
  { code: "zh", label: "中文" },
  { code: "hi", label: "हिन्दी" },
  { code: "ar", label: "العربية", dir: "rtl" },
  { code: "yo", label: "Yorùbá" },
  { code: "ig", label: "Igbo" },
  { code: "ha", label: "Hausa" },
];

const STORAGE_KEY = "smarttech-lang";

// Deliberately always "en" here, even in the browser -- NOT read from localStorage at init
// time. This app renders via Pages Router SSR (getServerSideProps on several routes this is
// now wired into: dashboard chrome, navbar), and the server has no access to localStorage, so
// it always renders English. If the client's *first* render read localStorage synchronously
// (as an earlier version of this file did) and started in "es", React would hydrate mismatched
// text on every hard navigation/refresh while a non-English language was active (a blocking
// dev-mode "Text content does not match server-rendered HTML" overlay, and a real flash+warning
// in production) -- exactly the failure mode context/Context.js's theme avoids by applying its
// persisted localStorage value to document.body in a useEffect *after* mount instead of during
// the initial render. restorePersistedLanguage() below does the equivalent for language: it's
// called once from pages/_app.js's mount effect, so both server and the client's first paint
// agree on "en", and the persisted choice applies a tick later, exactly like the theme class.
function getInitialLanguage() {
  return "en";
}

if (!i18n.isInitialized) {
  i18n.use(initReactI18next).init({
    lng: getInitialLanguage(),
    fallbackLng: "en",
    resources: {
      en: { translation: en, home: enHome, faq: enFaq, blog: enBlog, events: enEvents, dashboard: enDashboard, legal: enLegal, certificate: enCertificate },
      es: { translation: es, home: esHome, faq: esFaq, blog: esBlog, events: esEvents, dashboard: esDashboard, legal: esLegal, certificate: esCertificate },
      "es-AR": { translation: esAR, home: esARHome, faq: esARFaq, blog: esARBlog, events: esAREvents, dashboard: esARDashboard, legal: esARLegal, certificate: esARCertificate },
      "pt-BR": { translation: ptBR, home: ptBRHome, faq: ptBRFaq, blog: ptBRBlog, events: ptBREvents, dashboard: ptBRDashboard, legal: ptBRLegal, certificate: ptBRCertificate },
      ar: { translation: ar, home: arHome, faq: arFaq, blog: arBlog, events: arEvents, dashboard: arDashboard, legal: arLegal, certificate: arCertificate },
      fr: { translation: fr, home: frHome, faq: frFaq, blog: frBlog, events: frEvents, dashboard: frDashboard, legal: frLegal, certificate: frCertificate },
      de: { translation: de, home: deHome, faq: deFaq, blog: deBlog, events: deEvents, dashboard: deDashboard, legal: deLegal, certificate: deCertificate },
      nl: { translation: nl, home: nlHome, faq: nlFaq, blog: nlBlog, events: nlEvents, dashboard: nlDashboard, legal: nlLegal, certificate: nlCertificate },
      pt: { translation: pt, home: ptHome, faq: ptFaq, blog: ptBlog, events: ptEvents, dashboard: ptDashboard, legal: ptLegal, certificate: ptCertificate },
      ru: { translation: ru, home: ruHome, faq: ruFaq, blog: ruBlog, events: ruEvents, dashboard: ruDashboard, legal: ruLegal, certificate: ruCertificate },
      zh: { translation: zh, home: zhHome, faq: zhFaq, blog: zhBlog, events: zhEvents, dashboard: zhDashboard, legal: zhLegal, certificate: zhCertificate },
      hi: { translation: hi, home: hiHome, faq: hiFaq, blog: hiBlog, events: hiEvents, dashboard: hiDashboard, legal: hiLegal, certificate: hiCertificate },
      yo: { translation: yo, home: yoHome, faq: yoFaq, blog: yoBlog, events: yoEvents, dashboard: yoDashboard, legal: yoLegal, certificate: yoCertificate },
      ig: { translation: ig, home: igHome, faq: igFaq, blog: igBlog, events: igEvents, dashboard: igDashboard, legal: igLegal, certificate: igCertificate },
      ha: { translation: ha, home: haHome, faq: haFaq, blog: haBlog, events: haEvents, dashboard: haDashboard, legal: haLegal, certificate: haCertificate },
    },
    ns: ["translation", "home", "faq", "blog", "events", "dashboard", "legal", "certificate"],
    defaultNS: "translation",
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
    // All resources are bundled inline above (no backend HTTP fetch), so init() has everything
    // it needs to finish synchronously -- but i18next still defers its internal "initialized"
    // callback to the next tick by default (via setImmediate/setTimeout), which on this SSR
    // (Pages Router getServerSideProps) app caused a real hydration bug: server-rendered HTML
    // would contain the raw key ("settings.title") because renderToString ran before that tick,
    // while the client's first paint ran after it and showed the real translation ("Settings"),
    // producing a text-content mismatch. initImmediate: false makes init() resolve
    // synchronously in this bundled-resources case, so server and client always agree.
    initImmediate: false,
  });
}

// Keeps <html lang="..."> in sync with the active language -- matters for screen readers and
// search engines (was hardcoded "en" in pages/_document.js forever, which can only ever render
// the server's initial guess since Document only runs server-side; this is the client-side
// update that keeps it honest after a language switch). Mirrors context/Context.js's own
// pattern of applying a persisted/detected choice to the document element from a client-side
// effect.
//
// Deliberately does NOT set <html dir="...">. An earlier version did, which got Arabic's own
// text reading right-to-left correctly, but the theme's flex-heavy layout (nav, sidebar,
// buttons, grids -- ~20+ bare `display:flex` rules with no flex-direction pinned) natively
// mirrors under a root-level dir="rtl" per the CSS spec, while separate hardcoded
// text-align:left/right rules in the same stylesheets don't flip along with it -- producing an
// inconsistent, broken-looking "inverted" site rather than a properly mirrored RTL layout.
// Properly finishing a mirrored RTL layout (auditing every directional rule in the theme) is a
// real, separate project. Until that happens, isRtlLanguage() below lets individual components
// opt a specific prose container (legal page body, blog article body, FAQ answers, course/event
// descriptions -- the places Arabic readers actually read paragraphs of translated text) into
// dir="rtl" locally, so those read correctly while the site's structural chrome stays LTR and
// stops mirroring.
function applyDocumentDirection(code) {
  if (typeof document === "undefined") return;
  document.documentElement.lang = code;
}

// Whether `code` reads right-to-left -- see the comment above applyDocumentDirection() for why
// this drives a local `dir="rtl"` on specific prose containers instead of the document root.
export function isRtlLanguage(code) {
  const lang = SUPPORTED_LANGUAGES.find((l) => l.code === code);
  return lang?.dir === "rtl";
}

// Changes the active language and persists the choice, so it's remembered on the next visit --
// used by LanguageSwitcher (Settings), LanguageMenu (navbar) and available for any future
// consumer. `persist: false` is used only by restorePersistedLanguage() below, to re-apply an
// already-stored choice without rewriting the same value back to localStorage.
export function changeLanguage(code, { persist = true } = {}) {
  if (!SUPPORTED_LANGUAGES.some((l) => l.code === code)) return;
  i18n.changeLanguage(code);
  applyDocumentDirection(code);
  if (!persist) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, code);
  } catch (e) {
    // ignore -- language still applies for this session even if it can't be persisted.
  }
}

// Picks the best entry in SUPPORTED_LANGUAGES for a single BCP-47 tag the browser reports (e.g.
// "fr-CA", "es-MX", "pt", "zh-Hans-CN"), or null if nothing matches at all. Two passes: exact
// code match first (so a browser reporting our exact "es-AR" gets that, not generic "es"), then
// a base-language match (a browser's "es-MX" has no "es-MX" entry here, but should still land on
// generic "es" rather than falling through to English) -- checked in that order so an exact
// region match always wins over a same-base-language generic one.
function matchSupportedLanguage(tag) {
  if (!tag) return null;
  const lower = tag.toLowerCase();
  const exact = SUPPORTED_LANGUAGES.find((l) => l.code.toLowerCase() === lower);
  if (exact) return exact.code;

  // Base-language fallback (a browser reporting "pt-PT" or "es-MX" has no exact entry here, but
  // should still land on the right *generic* language) -- prefer the bare-code entry ("pt", not
  // "pt-BR") when both exist, so e.g. a Portugal-Portuguese browser doesn't get matched to
  // Brazilian Portuguese just because that regional variant happens to sit earlier in
  // SUPPORTED_LANGUAGES than the generic one. Only fall through to *some* regional variant of
  // the base language if no bare-code entry exists for it at all.
  const base = lower.split("-")[0];
  const bareMatch = SUPPORTED_LANGUAGES.find((l) => l.code.toLowerCase() === base);
  if (bareMatch) return bareMatch.code;

  const regionalMatch = SUPPORTED_LANGUAGES.find((l) => l.code.toLowerCase().split("-")[0] === base);
  return regionalMatch ? regionalMatch.code : null;
}

// Detects the visitor's preferred language straight from the browser -- no IP-geolocation
// service, no backend call, nothing third-party: `navigator.languages` (Chrome/Firefox/Edge)
// is the browser's own ranked list of the user's OS/browser language preferences, richer than
// the older single-value `navigator.language` (which every browser also supports, used here
// only as a fallback for the rare case `navigator.languages` is unavailable). Walks the list in
// the user's own preference order and returns the first one this app actually supports, or null
// if none of their preferred languages are supported (caller keeps the "en" default in that case).
export function detectBrowserLanguage() {
  if (typeof window === "undefined" || typeof navigator === "undefined") return null;

  const candidates = Array.isArray(navigator.languages) && navigator.languages.length
    ? navigator.languages
    : navigator.language
      ? [navigator.language]
      : [];

  for (const tag of candidates) {
    const match = matchSupportedLanguage(tag);
    if (match) return match;
  }
  return null;
}

// Applies a previously-persisted language choice, if any -- meant to be called once from a
// client-only useEffect (pages/_app.js) after mount, never during the initial render. See the
// getInitialLanguage() comment above for why this is split out instead of being read synchronously
// at i18n.init() time.
//
// When there's no stored choice at all (a first-ever visit, or localStorage was cleared), falls
// back to auto-detecting from the browser's own language preferences via detectBrowserLanguage()
// instead of silently defaulting to English -- and persists whatever it lands on (detected or
// English), so this detection only ever runs once per visitor; from then on it's just their
// stored choice like any explicit pick from the language switcher, and switching languages
// manually always overrides it exactly like it would override a first-visit guess.
export function restorePersistedLanguage() {
  if (typeof window === "undefined") return;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED_LANGUAGES.some((l) => l.code === stored)) {
      if (stored !== i18n.language) changeLanguage(stored, { persist: false });
      return;
    }

    const detected = detectBrowserLanguage();
    if (detected && detected !== i18n.language) {
      changeLanguage(detected);
    } else if (detected) {
      // already on "en" and that's also what was detected -- still worth persisting so this
      // detection pass doesn't re-run (and re-evaluate navigator.languages) on every visit.
      window.localStorage.setItem(STORAGE_KEY, detected);
    }
  } catch (e) {
    // localStorage unavailable (private mode, etc.) -- detection still applies for this session
    // via changeLanguage(..., {persist:false}) below, just won't be remembered next visit.
    const detected = detectBrowserLanguage();
    if (detected && detected !== i18n.language) changeLanguage(detected, { persist: false });
  }
}

export default i18n;
